import { Stack } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { MessageSquare } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import {
  MergeEntityCard,
  type MergeEntitySummary,
  MergeTargetPicker,
} from 'src/components/MergeTargetPicker';
import { ModalContainer } from 'src/components/ModalContainer';
import { Conversation, useCurrentUserProfile, useMergeConversation } from 'src/graphql';
import { getRequesterDisplayName } from 'src/graphql/entities/conversation/conversation.mappings.utils';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { formatDisplayDate } from 'src/utils/date';
import { useNotifications } from 'src/utils/hooks';
import { getFocusableElements } from 'src/utils/html';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { MergeTargetSuggestions } from './merge-target-suggestions';
import { useConversationTargetSearch } from './use-conversation-target-search';

const CONVERSATION_ICON = <MessageSquare size={16} />;

const toConversationSummary = (conversation: Conversation): MergeEntitySummary => {
  const requester = getRequesterDisplayName(conversation.requesterFullName);
  const created = `Created ${formatDisplayDate(conversation.createdAt)}`;

  return {
    id: conversation.id,
    title: `${conversation.friendlyId} | ${conversation.subject}`,
    secondary: requester ? `${requester} · ${created}` : created,
  };
};

export const MergeConversationModal = () => {
  const { closeModal } = useModalMethods(Modals.MergeConversationModal);
  const { isOpened, modalProps } = useModalState(Modals.MergeConversationModal);
  const [conversationTarget, setConversationTarget] = useState<Conversation | null>(null);
  const { customViewId } = useRouteParams(UrlParams.customViewId);
  const { mergeConversation } = useMergeConversation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const { conversation } = modalProps ?? {};

  const { records, loading, hasMore, loadMore, setSearchTerm } =
    useConversationTargetSearch(conversation);

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const { navigate } = useNavigateRoute();

  const { user } = useCurrentUserProfile();

  const selectConversation = useCallback(
    (conversation: Conversation) => {
      navigate(
        portalPaths.entity.all({
          entityName: 'conversations',
          customViewId,
          id: conversation?.friendlyId,
        }),
      );
    },
    [navigate, customViewId],
  );

  const closeMergeModal = useCallback(() => {
    closeModal();
    setConversationTarget(null);
    setSearchTerm('');
  }, [closeModal, setSearchTerm]);

  const handleMergeConversations = useCallback(async () => {
    if (!conversation?.id) {
      showErrorAlert(`Invalid conversation target ${conversation?.id}`);

      return;
    }

    if (!conversationTarget?.id) {
      showErrorAlert(`Invalid conversation origin ${conversationTarget?.id}`);

      return;
    }

    closeMergeModal();

    const response = await mergeConversation({
      conversationTargetId: conversationTarget?.id,
      friendlyConversationTargetId: conversationTarget.friendlyId,
      conversationOriginId: conversation?.id,
      friendlyConversationOriginId: conversation.friendlyId,
      user,
    });

    if (!response) {
      return;
    }

    if (response.error) {
      console.error(response.error);
      showErrorAlert(response.error);
      return;
    }

    showSuccessAlert('Conversations were merged');
    setConversationTarget(null);
    selectConversation(conversationTarget);
  }, [
    closeMergeModal,
    conversation,
    conversationTarget,
    mergeConversation,
    selectConversation,
    showErrorAlert,
    showSuccessAlert,
    user,
  ]);

  const focusFirstSuggestion = useCallback(() => {
    getFocusableElements(suggestionsRef.current)?.[0]?.focus();
  }, []);

  const focusSearch = useCallback(() => {
    searchInputRef.current?.focus();
  }, []);

  const showSuggestions = Boolean(conversation && !conversationTarget);

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Merge conversations"
      onClose={closeMergeModal}
      actions={[
        { label: 'Cancel', kind: 'secondary' },
        {
          label: 'Merge conversations',
          kind: 'primary',
          onClick: handleMergeConversations,
          disabled: !conversation || !conversationTarget,
          testId: 'merge-conversation-submit',
        },
      ]}
      data-testid="merge-conversation-modal"
    >
      <Stack gap={2}>
        {conversation ? (
          <MergeEntityCard
            label="From"
            icon={CONVERSATION_ICON}
            summary={toConversationSummary(conversation)}
            testId="merge-origin-card"
          />
        ) : null}
        <MergeTargetPicker
          label="Into"
          placeholder="Search conversations"
          icon={CONVERSATION_ICON}
          records={records}
          target={conversationTarget}
          toSummary={toConversationSummary}
          onTargetChange={setConversationTarget}
          onSearchChange={setSearchTerm}
          loading={loading}
          hasMore={hasMore}
          loadMore={loadMore}
          searchInputRef={searchInputRef}
          onArrowDownFromEmptySearch={showSuggestions ? focusFirstSuggestion : undefined}
        />
        {conversation && showSuggestions ? (
          <MergeTargetSuggestions
            listRef={suggestionsRef}
            origin={conversation}
            onSelect={setConversationTarget}
            onExitTop={focusSearch}
          />
        ) : null}
      </Stack>
    </ModalContainer>
  );
};
